import React, { useState, useRef, useCallback, useEffect } from "react"
import DealerSearch from "./DealerSearch"
import { CloseOutline20 } from "@carbon/icons-react"
import {
  GoogleMap,
  useLoadScript,
  Marker,
  InfoWindow,
} from "@react-google-maps/api"
import { Renew20 } from "@carbon/icons-react"
import mapStyles from "../../utilities/mapStyles"
import { useTranslation } from "react-i18next"
import DealerDetails, { LocationDetails } from "./DealerDetails"

function inputIsAddress(types) {
  const validAddressTypesForDistance = [
    "street_address",
    "sublocality",
    "sublocality_level_1",
    "postal_code",
    "premise",
    "locality",
  ]
  return validAddressTypesForDistance.some((type) => types.includes(type))
}

function inputZoomAmount(types) {
  const zoomLevels = {
    street_address: 16,
    premise: 16,
    sublocality: 14,
    postal_code: 14,
    locality: 12,
    administrative_area: 10,
    country: 8,
  }

  for (const type of types) {
    if (zoomLevels[type] !== undefined) {
      return zoomLevels[type]
    }
  }

  return 8
}

const locationMarkerIcon = () => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="44" viewBox="0 0 32 44" fill="none">
      <g>
        <path d="M32 17.0625C32 26.4688 16 43.9375 16 43.9375C16 43.9375 0 26.4688 0 17.0625C0 8.16016 7.16667 0.9375 16 0.9375C24.8333 0.9375 32 8.16016 32 17.0625Z" fill="#E60000"/>
      </g>
    </svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

export const dealerMarkerIcon = (distanceIdx, variant = "marker") => {
  const number =
    distanceIdx != null && distanceIdx < MAX_AMOUNT ? distanceIdx + 1 : null
  const size = number ? "30" : "20" // smaller icon without number
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <path d="M0,0 L0,${size} L${size},${size} L${size},0 Z"
        fill="#e60000"
        fill-opacity="1.0"
        stroke="#FFFFFF"
        stroke-width="2.0"
        stroke-opacity="1"
      />
      <foreignObject x="0" y="0" width="100%" height="100%">
        <div xmlns="http://www.w3.org/1999/xhtml"
          style="
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 100%;
            font-size: 16px;
            color: white;
            font-family: 'Roboto', sans-serif;">
          ${number ?? ""}
        </div>
      </foreignObject>
    </svg>
  `
  if (variant === "marker") {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
  } else {
    return <div dangerouslySetInnerHTML={{ __html: svg }}></div>
  }
}

const containerStyle = {
  display: "block",
  width: "100vw",
  height: "100vh",
}

const defaultMapPosition = {
  lat: 48.7814501,
  lng: 9.1775569,
}

const DEFAULT_AMOUNT = 3
const MAX_AMOUNT = 15

export function formatDistance(distance) {
  if (distance) {
    return `${distance.toString().replace(".", ",")} km`
  } else {
    return "-"
  }
}

const DealerMap = ({
  initialQuery,
  setSearchMode,
  searchMode,
  coords,
  error,
  containerWidth,
  containerHeight,
}) => {
  const { t, i18n } = useTranslation()
  const [mapLoaded, setMapLoaded] = useState(false)
  const [dealerData, setDealerData] = useState(null)
  const [mapPosition, setMapPosition] = useState(defaultMapPosition)
  const [zoom, setZoom] = useState(initialQuery ? 6 : 8)
  const [formattedAddress, setFormattedAddress] = useState("")
  const [selectedMarker, setSelectedMarker] = useState(null)
  const [visibleMarkers, setVisibleMarkers] = useState([])
  const [hideError, setHideError] = useState(false)
  const [visibleNearbyDealersAmount, setVisibleNearbyDealersAmount] =
    useState(DEFAULT_AMOUNT)
  const [initialMarkerSearch, setInitialMarkerSearch] = useState(true)
  const [queryString, setQueryString] = useState(initialQuery)
  const [calculateDistance, setCalculateDistance] = useState(false)
  const [geometryLoaded, setGeometryLoaded] = useState(true)
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY,
    language: i18n.language,
  })

  const zoomOutInterval = useRef(null)
  let containerStyleSettings = Object.assign({}, containerStyle)

  const displayMoreButton =
    calculateDistance &&
    visibleNearbyDealersAmount < visibleMarkers.length &&
    visibleNearbyDealersAmount < MAX_AMOUNT &&
    dealerData?.dealer?.length > 0

  function increaseAmountDisplayedDealers() {
    if (visibleMarkers.length - visibleNearbyDealersAmount >= 3) {
      setVisibleNearbyDealersAmount((prev) =>
        prev + 3 >= MAX_AMOUNT ? MAX_AMOUNT : prev + 3
      )
    } else {
      setVisibleNearbyDealersAmount((prev) =>
        prev + (visibleMarkers.length - visibleNearbyDealersAmount) >=
        MAX_AMOUNT
          ? MAX_AMOUNT
          : prev + visibleMarkers.length - visibleNearbyDealersAmount
      )
    }
  }

  const loadDealerData = useCallback(async () => {
    const response = await fetch(process.env.GATSBY_DEALER_SEARCH_URL)
    const result = await response.json()
    const dealerFiltered = result.locations.filter(
      (dealer) => dealer.status === 1
    )
    setDealerData({ dealer: dealerFiltered })
  }, [])

  if (containerWidth) {
    containerStyleSettings.width = containerWidth
  }
  if (containerHeight) {
    containerStyleSettings.height = containerHeight
  }

  const options = {
    styles: mapStyles,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    zoomControlOptions: {
      position: isLoaded && google.maps.ControlPosition.RIGHT_CENTER,
    },
  }

  const mapRef = useRef()
  const onMapLoad = useCallback((map) => {
    mapRef.current = map
    setMapLoaded(true)
    getVisibleMarkers()
  }, [])

  // pan map to search input
  const geoByInput = useCallback(() => {
    if (mapLoaded && queryString) {
      const geocoder = new google.maps.Geocoder()
      geocoder.geocode(
        { address: decodeURIComponent(queryString) },
        async (results) => {
          try {
            const result = results[0]
            const address = result.formatted_address
            const addressTypes = result.types
            const lat = await result.geometry.location.lat()
            const lng = await result.geometry.location.lng()
            setCalculateDistance(inputIsAddress(addressTypes))
            setFormattedAddress(address)
            setMapPosition({ lat, lng })
            panTo({ lat, lng })
            setZoom(inputZoomAmount(addressTypes))
            // force map marker redraw...
            setTimeout(() => {
              setZoom((prevZoom) => Math.max(prevZoom - 1, 0))
              setZoom((prevZoom) => Math.max(prevZoom + 1, 0))
            }, 200)
          } catch (error) {
            console.error(error)
          }
        }
      )
    }
  }, [mapLoaded, queryString])

  const geoByCoords = useCallback(() => {
    if (mapLoaded) {
      const geocoder = new google.maps.Geocoder()
      geocoder.geocode({ location: coords }, async (results) => {
        try {
          const result = results[0]
          const address = result.formatted_address
          const addressTypes = result.types
          const lat = await result.geometry.location.lat()
          const lng = await result.geometry.location.lng()
          setCalculateDistance(inputIsAddress(addressTypes))
          setFormattedAddress(address)
          setMapPosition({ lat, lng })
          setZoom(inputZoomAmount(["street_address"]))
          panTo({ lat, lng })
          // force map marker redraw...
          setTimeout(() => {
            setZoom((prevZoom) => Math.max(prevZoom - 1, 0))
            setZoom((prevZoom) => Math.max(prevZoom + 1, 0))
          }, 200)
        } catch (error) {
          console.error(error)
        }
      })
    }
  }, [mapLoaded, coords])

  useEffect(() => {
    setVisibleMarkers([])
    setVisibleNearbyDealersAmount(DEFAULT_AMOUNT)
  }, [formattedAddress])

  const panTo = ({ lat, lng }) => {
    mapRef.current.panTo({ lat, lng })
  }

  // get all dealers in current bounds when map is idle
  let visibleDealers = []
  const getVisibleMarkers = () => {
    visibleDealers.length > 0 && visibleDealers.splice(0, visibleDealers.length)
    if (mapRef.current) {
      const bounds = mapRef.current.getBounds()

      dealerData?.dealer.forEach((dealer) => {
        const markerPosition = {
          lat: parseFloat(dealer.latitude),
          lng: parseFloat(dealer.longitude),
        }
        if (bounds && bounds.contains(markerPosition)) {
          // if the user searched for a location
          // calc the geo distance for each visible marker
          if (mapPosition && geometryLoaded && calculateDistance) {
            const distance =
              google.maps.geometry.spherical.computeDistanceBetween(
                markerPosition,
                mapPosition
              )
            dealer.distanceValue = parseFloat((distance / 1000.0).toFixed(2)) // in kilometers
          }
          visibleDealers.push(dealer)
        }
      })
      if (calculateDistance) {
        let sortedVisibleDealers = [...visibleDealers, ...visibleMarkers]

        sortedVisibleDealers = sortedVisibleDealers.filter(
          (obj1, i, arr) => arr.findIndex((obj2) => obj2.id === obj1.id) === i
        )

        sortedVisibleDealers = sortedVisibleDealers
          .sort((a, b) => a.distanceValue - b.distanceValue)
          .map((m, i) => {
            return {
              ...m,
              ...{ distanceIdx: i },
            }
          })

        sortedVisibleDealers.forEach((d) => {
          const inD = dealerData?.dealer.find(
            (innerDealer) => innerDealer.id === d.id
          )
          if (inD) {
            inD.distanceIdx = d.distanceIdx
          }
        })
        setVisibleMarkers(sortedVisibleDealers)
      }
    }
  }

  useEffect(() => {
    if (mapLoaded && visibleMarkers.length === 0 && dealerData) {
      if (zoomOutInterval.current) {
        clearTimeout(zoomOutInterval.current)
      }
      zoomOutInterval.current = setTimeout(() => {
        setZoom((prevZoom) => Math.max(prevZoom - 1, 0))
      }, 200)
    }
    return () => {
      if (zoomOutInterval.current) clearTimeout(zoomOutInterval.current)
    }
  }, [mapLoaded, mapPosition, visibleMarkers, dealerData])

  async function loadGeometryLib() {
    await google.maps.importLibrary("geometry")
    setGeometryLoaded(true)
  }

  useEffect(() => {
    if (isLoaded) {
      loadGeometryLib()
    }
  }, [isLoaded])

  useEffect(() => {
    loadDealerData()
  }, [mapPosition, loadDealerData])

  useEffect(() => {
    if (mapLoaded) {
      if (searchMode === "query") {
        geoByInput()
      } else if (searchMode === "geolocation" && coords) {
        geoByCoords()
      }
    }
  }, [searchMode, mapRef, mapLoaded, coords, geoByInput, geoByCoords])

  useEffect(() => {
    if (mapLoaded) {
      //setHideError(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  // if the user blocked tracking the default map will be rendered
  if (loadError) return <div>error</div>
  if (!isLoaded)
    return (
      <div className="py-block block-w-container">
        <Renew20 className="animate-spin transform rotate-180 inline-block -mt-0.5 align-middle" />
      </div>
    )

  if (isLoaded)
    return (
      <>
        <section className="flex flex-col w-full h-full bg-gray-100">
          {error && hideError === false && (
            <div className="w-full max-w-screen-xl mx-auto px-grid">
              <div
                class="bg-red-100 text-sm border border-red-400 text-red-700 px-4 py-3 my-2 relative"
                role="alert"
              >
                <span class="block">{t(error)}</span>
                <button
                  className="absolute z-20 right-2 top-2"
                  onClick={() => setHideError(true)}
                >
                  <CloseOutline20 />
                </button>
              </div>
            </div>
          )}
          <div className="flex flex-col items-center justify-center w-full h-full max-w-screen-xl gap-16 px-4 py-16 mx-auto lg:flex-row">
            <DealerSearch
              className="flex flex-col w-full gap-8 lg:w-1/3"
              activeAddress={formattedAddress}
              setQueryString={setQueryString}
              setSearchMode={setSearchMode}
            />
            <div className="w-full lg:w-2/3 aspect-[4/3] h-auto max-h-full -py-16 flex relative">
              <GoogleMap
                mapContainerStyle={{ width: "100%", height: "100%" }}
                center={mapPosition}
                zoom={zoom}
                options={options}
                onLoad={onMapLoad}
                onIdle={getVisibleMarkers}
              >
                {calculateDistance && (
                  <Marker icon={locationMarkerIcon()} position={mapPosition} />
                )}
                {dealerData?.dealer.map((marker, i) => (
                  <Marker
                    key={marker.id}
                    position={{
                      lat: parseFloat(marker.latitude),
                      lng: parseFloat(marker.longitude),
                    }}
                    icon={dealerMarkerIcon(marker.distanceIdx, "marker")}
                    onClick={() => {
                      setSelectedMarker(marker.id)
                    }}
                    optimized={false}
                    zIndex={99999}
                  >
                    {marker.id === selectedMarker ? (
                      <InfoWindow
                        onCloseClick={() => {
                          setSelectedMarker(null)
                        }}
                      >
                        <DealerDetails
                          number={marker.distanceIdx}
                          className="flex flex-col gap-1 w-80 min-h-fit"
                          marker={marker}
                          variant="infoWindow"
                        />
                      </InfoWindow>
                    ) : null}
                  </Marker>
                ))}
              </GoogleMap>
            </div>
          </div>
        </section>
        {calculateDistance && (
          <section className="w-full md:my-16">
            <div className="hidden w-full max-w-screen-xl px-4 mx-auto md:block">
              <table className="w-full h-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="w-1/3 px-6 py-4 border-r text-start">
                      {t("generic.dealer_table.distance")}
                    </th>
                    <th className="w-1/3 px-6 py-4 border-r text-start">
                      {t("generic.dealer_table.location")}
                    </th>
                    <th className="w-1/3 px-6 py-4 text-start">
                      {t("generic.dealer_table.links")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleMarkers
                    .slice(0, visibleNearbyDealersAmount)
                    .map((marker) => (
                      <tr className="border-b last:border-b-0" key={marker.id}>
                        <td className="p-6 border-r">
                          <div className="flex items-center gap-2">
                            {dealerMarkerIcon(marker.distanceIdx, "table")}
                            <span className="font-semibold">
                              {formatDistance(marker.distanceValue)}
                            </span>
                          </div>
                        </td>
                        <td className="p-6 border-r">
                          <DealerDetails marker={marker} variant="table" />
                        </td>
                        <td className="p-6">
                          <LocationDetails
                            address={marker.address}
                            postcode={marker.postcode}
                            city={marker.city}
                            latitude={marker.latitude}
                            longitude={marker.longitude}
                            variant="table"
                          >
                            {t("generic.dealer_table.routeButtonLabel")}
                          </LocationDetails>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {calculateDistance &&
              visibleMarkers
                .slice(0, visibleNearbyDealersAmount)
                .map((marker) => (
                  <div
                    className="flex flex-col p-5 border-b md:hidden "
                    key={marker.id}
                  >
                    <div className="flex items-center gap-2">
                      {dealerMarkerIcon(marker.distanceIdx, "table")}
                      <span className="font-semibold">
                        {formatDistance(marker.distanceValue)}
                      </span>
                    </div>
                    <DealerDetails marker={marker} variant="table" />
                    <LocationDetails
                      address={marker.address}
                      postcode={marker.postcode}
                      city={marker.city}
                      latitude={marker.latitude}
                      longitude={marker.longitude}
                      variant="table"
                    >
                      {t("generic.dealer_table.routeButtonLabel")}
                    </LocationDetails>
                  </div>
                ))}
            {displayMoreButton && (
              <button
                onClick={increaseAmountDisplayedDealers}
                className="block mt-5 mb-5 btn btn-secondary w-fit md:mb-5 md:mt-16 md:mx-auto"
              >
                {t("generic.dealer_table.displayMoreButtonLabel")}
              </button>
            )}
          </section>
        )}
      </>
    )
}

export default DealerMap
